"use client";

// src/auth-utils.ts
function getCookie(name) {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length >= 2) {
    const val = parts.pop()?.split(";").shift()?.trim();
    return val && val !== "null" && val !== "undefined" && val !== '""' ? val : null;
  }
  return null;
}
function getToken() {
  if (typeof window === "undefined") return null;
  if (typeof document !== "undefined") {
    const cookieToken = getCookie("jwt_token") || getCookie("authToken");
    if (cookieToken) {
      if (localStorage.getItem("jwt_token") !== cookieToken) {
        localStorage.setItem("jwt_token", cookieToken);
      }
      if (localStorage.getItem("authToken") !== cookieToken) {
        localStorage.setItem("authToken", cookieToken);
      }
      return cookieToken;
    } else {
      if (localStorage.getItem("jwt_token") || localStorage.getItem("authToken")) {
        localStorage.removeItem("jwt_token");
        localStorage.removeItem("authToken");
      }
      return null;
    }
  }
  const localToken = localStorage.getItem("jwt_token") || localStorage.getItem("authToken");
  return localToken && localToken !== "null" && localToken !== "undefined" ? localToken : null;
}
function getCookieDomain() {
  if (typeof window === "undefined") return "";
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.startsWith("192.168.") || hostname.startsWith("10.")) {
    return "";
  }
  if (hostname.endsWith("jabeztech.com")) {
    return "; domain=.jabeztech.com";
  }
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    const rootDomain = parts.slice(-2).join(".");
    return `; domain=.${rootDomain}`;
  }
  return "";
}
function getCookieSecureAttr() {
  if (typeof window === "undefined") return "";
  return window.location.protocol === "https:" ? "; Secure" : "";
}
function setToken(token) {
  if (typeof window !== "undefined") {
    localStorage.setItem("jwt_token", token);
    localStorage.setItem("authToken", token);
    if (typeof document !== "undefined") {
      const domainAttr = getCookieDomain();
      const secureAttr = getCookieSecureAttr();
      document.cookie = `jwt_token=${token}; path=/; max-age=31536000; SameSite=Lax${secureAttr}${domainAttr}`;
      document.cookie = `authToken=${token}; path=/; max-age=31536000; SameSite=Lax${secureAttr}${domainAttr}`;
      if (domainAttr) {
        document.cookie = `jwt_token=${token}; path=/; max-age=31536000; SameSite=Lax${secureAttr}`;
        document.cookie = `authToken=${token}; path=/; max-age=31536000; SameSite=Lax${secureAttr}`;
      }
    }
  }
}
function removeToken() {
  if (typeof window !== "undefined") {
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("user_id");
    localStorage.removeItem("auth-storage");
    sessionStorage.removeItem("auth-storage");
    if (typeof document !== "undefined") {
      const domainAttr = getCookieDomain();
      const secureAttr = getCookieSecureAttr();
      const names = ["jwt_token", "authToken", "user_id"];
      names.forEach((name) => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; SameSite=Lax`;
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; SameSite=Lax${secureAttr}`;
        if (domainAttr) {
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; SameSite=Lax${domainAttr}`;
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; SameSite=Lax${secureAttr}${domainAttr}`;
        }
      });
    }
  }
}
function getAuthUrl() {
  if (typeof window === "undefined") return "https://auth.jabeztech.com";
  const envAuthUrl = typeof import.meta !== "undefined" && import.meta.env?.VITE_AUTH_URL || window.VITE_AUTH_URL;
  if (envAuthUrl) return envAuthUrl;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return "http://localhost:4001";
  }
  return "https://auth.jabeztech.com";
}

// src/auth-store.ts
import { Roles } from "@repo/rbac";
import { create } from "zustand";
import { persist } from "zustand/middleware";
var normalizeRoles = (roles) => {
  if (Array.isArray(roles)) return roles;
  if (roles == null) return [];
  if (typeof roles === "string") {
    try {
      const parsed = JSON.parse(roles);
      return Array.isArray(parsed) ? parsed : [roles];
    } catch {
      return [roles];
    }
  }
  return [];
};
var useAuthStore = create()(
  persist(
    (set) => ({
      user: null,
      token: null,
      login: (user, token) => {
        setToken(token);
        if (typeof window !== "undefined") {
          localStorage.setItem("user_id", String(user?.userId ?? ""));
          if (typeof document !== "undefined") {
            const domainAttr = getCookieDomain();
            document.cookie = `user_id=${String(user?.userId ?? "")}; path=/; max-age=31536000; SameSite=Lax${domainAttr}`;
            if (domainAttr) {
              document.cookie = `user_id=${String(user?.userId ?? "")}; path=/; max-age=31536000; SameSite=Lax`;
            }
          }
        }
        const roles = normalizeRoles(user?.roles);
        set({
          token,
          user: {
            ...user,
            roles,
            isSuperAdmin: roles.includes(Roles.ROLE_ADMIN) || roles.includes("ROLE_SUPERADMIN"),
            isEventAdmin: roles.includes(Roles.ROLE_EVENT_ADMIN),
            isEventAccount: roles.includes(Roles.ROLE_EVENT_ACCOUNT)
          }
        });
      },
      logout: () => {
        set({ user: null, token: null });
        removeToken();
        if (typeof window !== "undefined") {
          localStorage.removeItem("user_id");
        }
      }
    }),
    {
      name: "auth-storage"
    }
  )
);

// src/trpc.ts
import { createTRPCReact } from "@trpc/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import superjson from "superjson";
var trpc = createTRPCReact();
var getBaseUrl = () => {
  const url = import.meta?.env?.VITE_API_URL || "https://api.jabeztech.com";
  console.log("[api-client] Base URL:", url);
  return url;
};
var trpcClient = createTRPCClient({
  links: [
    httpBatchLink({
      url: `${getBaseUrl()}/trpc`,
      transformer: superjson,
      headers: () => {
        const token = getToken();
        return {
          authorization: token ? `Bearer ${token}` : ""
        };
      },
      fetch: async (url, options) => {
        const response = await fetch(url, options);
        if (response.status === 401) {
          console.error("Unauthorized access - clearing token and logging out");
          removeToken();
          useAuthStore.getState().logout();
        }
        return response;
      }
    })
  ]
});
var createTrpcClient = (baseUrl) => createTRPCClient({
  links: [
    httpBatchLink({
      url: `${baseUrl}/trpc`,
      transformer: superjson,
      headers: () => {
        const token = getToken();
        return {
          authorization: token ? `Bearer ${token}` : ""
        };
      },
      fetch: async (url, options) => {
        const response = await fetch(url, options);
        if (response.status === 401) {
          removeToken();
        }
        return response;
      }
    })
  ]
});

// src/components/RequireAuth.tsx
import { hasPermission } from "@repo/rbac";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { TRPCClientError } from "@trpc/client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var RequireAuth = ({
  children,
  authUrl: propsAuthUrl,
  loadingComponent,
  permission
}) => {
  const { user, token: storeToken, login, logout } = useAuthStore();
  const token = getToken();
  const isTokenMismatch = Boolean(token && storeToken && token !== storeToken);
  const [isLoading, setIsLoading] = useState(!user || !token || isTokenMismatch);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoopDetected, setIsLoopDetected] = useState(false);
  const isChecking = useRef(false);
  const verifiedTokenRef = useRef(null);
  const redirectToAuth = useCallback(
    (reason) => {
      const authUrl = propsAuthUrl || getAuthUrl();
      if (!authUrl) {
        console.error("Auth URL not configured (VITE_AUTH_URL)");
        return;
      }
      const REDIRECT_RECORD_KEY = "auth_redirect_history";
      const now = Date.now();
      let history = [];
      try {
        const raw = sessionStorage.getItem(REDIRECT_RECORD_KEY);
        if (raw) {
          history = JSON.parse(raw);
          history = history.filter((t) => now - t < 8e3);
        }
      } catch {
        history = [];
      }
      if (history.length >= 3) {
        console.error(
          "[RequireAuth] Redirect loop detected: 3+ redirects in last 8s. Halting automatic redirect."
        );
        setIsLoopDetected(true);
        setIsLoading(false);
        return;
      }
      history.push(now);
      try {
        sessionStorage.setItem(REDIRECT_RECORD_KEY, JSON.stringify(history));
      } catch {
      }
      const redirectUri = encodeURIComponent(
        window.location.origin + window.location.pathname + window.location.search
      );
      const reasonParam = reason ? `&reason=${encodeURIComponent(reason)}` : "";
      window.location.href = `${authUrl}?redirect_uri=${redirectUri}${reasonParam}`;
    },
    [propsAuthUrl]
  );
  const checkAuth = useCallback(async () => {
    if (isChecking.current) {
      return;
    }
    isChecking.current = true;
    setErrorMessage(null);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("is_logging_out");
    }
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const storedToken = getToken();
    if (urlToken) {
      params.delete("token");
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState({}, "", newUrl);
      setToken(urlToken);
      try {
        const result = await trpcClient.auth.verifyJWT.query({
          token: urlToken
        });
        if (result?.userId) {
          verifiedTokenRef.current = urlToken;
          try {
            sessionStorage.removeItem("auth_redirect_history");
          } catch {
          }
          login(result, urlToken);
          setIsLoading(false);
          isChecking.current = false;
          return;
        } else {
          console.warn(
            "[RequireAuth] verifyJWT returned no userId for URL token"
          );
        }
      } catch (e) {
        const isAuthError = e instanceof TRPCClientError && (e.data?.code === "UNAUTHORIZED" || e.data?.httpStatus === 401 || e.message?.includes("Invalid or expired token"));
        if (isAuthError) {
          console.warn("[RequireAuth] URL token invalid or expired:", e.message);
          removeToken();
          logout();
          redirectToAuth("token_expired");
          isChecking.current = false;
          return;
        }
        console.error(
          "[RequireAuth] URL token verification network/server error:",
          e
        );
        setErrorMessage(
          "Unable to connect to the authentication server. Please check your network or try again."
        );
        setIsLoading(false);
        isChecking.current = false;
        return;
      }
      removeToken();
      logout();
      redirectToAuth("invalid_token");
      isChecking.current = false;
      return;
    }
    if (storedToken) {
      if (verifiedTokenRef.current === storedToken && user && (!storeToken || storeToken === storedToken)) {
        setIsLoading(false);
        isChecking.current = false;
        return;
      }
      setIsLoading(true);
      try {
        const result = await trpcClient.auth.verifyJWT.query({
          token: storedToken
        });
        if (result?.userId) {
          verifiedTokenRef.current = storedToken;
          try {
            sessionStorage.removeItem("auth_redirect_history");
          } catch {
          }
          login(result, storedToken);
          setIsLoading(false);
          isChecking.current = false;
          return;
        } else {
          console.warn(
            "[RequireAuth] verifyJWT returned no userId for stored token"
          );
        }
      } catch (e) {
        const isAuthError = e instanceof TRPCClientError && (e.data?.code === "UNAUTHORIZED" || e.data?.httpStatus === 401 || e.message?.includes("Invalid or expired token"));
        if (isAuthError) {
          console.warn(
            "[RequireAuth] Stored token invalid or expired:",
            e.message
          );
          removeToken();
          logout();
          redirectToAuth("session_expired");
          isChecking.current = false;
          return;
        }
        console.error(
          "[RequireAuth] Stored token verification failed with network/server error:",
          e
        );
        setErrorMessage(
          "Unable to connect to authentication server. Please try again."
        );
        setIsLoading(false);
        isChecking.current = false;
        return;
      }
    }
    if (!user || !storedToken) {
      removeToken();
      logout();
      redirectToAuth();
      isChecking.current = false;
      return;
    }
    setIsLoading(false);
    isChecking.current = false;
  }, [user, storeToken, login, logout, redirectToAuth]);
  useEffect(() => {
    checkAuth();
    const handleFocus = () => {
      if (isChecking.current) return;
      const activeToken = getToken();
      if (activeToken !== verifiedTokenRef.current) {
        checkAuth();
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
    };
  }, [checkAuth]);
  if (isLoopDetected) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
          fontFamily: "system-ui, -apple-system, sans-serif",
          padding: "24px",
          textAlign: "center"
        },
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              maxWidth: "440px",
              backgroundColor: "white",
              padding: "32px",
              borderRadius: "12px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
            },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: "#fee2e2",
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                    fontSize: "24px"
                  },
                  children: "!"
                }
              ),
              /* @__PURE__ */ jsx(
                "h2",
                {
                  style: {
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: "0 0 8px",
                    color: "#111827"
                  },
                  children: "Login Loop Detected"
                }
              ),
              /* @__PURE__ */ jsx(
                "p",
                {
                  style: {
                    color: "#6b7280",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    margin: "0 0 24px"
                  },
                  children: "An intermittent loop between the application and authentication service was prevented. Please clear your saved session and sign in again."
                }
              ),
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: { display: "flex", gap: "12px", justifyContent: "center" },
                  children: /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => {
                        sessionStorage.removeItem("auth_redirect_history");
                        removeToken();
                        logout();
                        const authUrl = propsAuthUrl || getAuthUrl();
                        const redirectUri = encodeURIComponent(
                          window.location.origin + window.location.pathname
                        );
                        window.location.href = `${authUrl}?redirect_uri=${redirectUri}&action=logout`;
                      },
                      style: {
                        backgroundColor: "#2563eb",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "10px 20px",
                        fontWeight: 600,
                        fontSize: "14px",
                        cursor: "pointer"
                      },
                      children: "Sign In Again"
                    }
                  )
                }
              )
            ]
          }
        )
      }
    );
  }
  if (errorMessage) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
          fontFamily: "system-ui, -apple-system, sans-serif",
          padding: "24px",
          textAlign: "center"
        },
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              maxWidth: "440px",
              backgroundColor: "white",
              padding: "32px",
              borderRadius: "12px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
            },
            children: [
              /* @__PURE__ */ jsx(
                "h2",
                {
                  style: {
                    fontSize: "20px",
                    fontWeight: 700,
                    margin: "0 0 8px",
                    color: "#111827"
                  },
                  children: "Connection Error"
                }
              ),
              /* @__PURE__ */ jsx(
                "p",
                {
                  style: {
                    color: "#6b7280",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    margin: "0 0 24px"
                  },
                  children: errorMessage
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: () => {
                    setErrorMessage(null);
                    setIsLoading(true);
                    checkAuth();
                  },
                  style: {
                    backgroundColor: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    padding: "10px 20px",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer"
                  },
                  children: "Retry"
                }
              )
            ]
          }
        )
      }
    );
  }
  if (isLoading || !user) {
    if (loadingComponent) return /* @__PURE__ */ jsx(Fragment, { children: loadingComponent });
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "white",
          fontFamily: "sans-serif"
        },
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "16px"
              },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "api-client-spinner",
                    style: {
                      width: "40px",
                      height: "40px",
                      border: "4px solid #2563eb",
                      borderTopColor: "transparent",
                      borderRadius: "50%"
                    }
                  }
                ),
                /* @__PURE__ */ jsx("p", { style: { color: "#6b7280", fontWeight: 500 }, children: isLoading ? "Verifying access..." : "Redirecting to login..." })
              ]
            }
          ),
          /* @__PURE__ */ jsx("style", { children: `
          .api-client-spinner { animation: spin 1s linear infinite; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        ` })
        ]
      }
    );
  }
  if (permission && user) {
    const permissions = Array.isArray(permission) ? permission : [permission];
    const hasPermissionResult = permissions.some(
      (p) => hasPermission(user.roles, p)
    );
    if (!hasPermissionResult) {
      return /* @__PURE__ */ jsx(Navigate, { to: "/unauthorized", replace: true });
    }
  }
  return children ? /* @__PURE__ */ jsx(Fragment, { children }) : /* @__PURE__ */ jsx(Outlet, {});
};

// src/usePermission.ts
import { hasPermission as hasPermission2 } from "@repo/rbac";
function usePermission(permission) {
  const { user } = useAuthStore();
  return user ? hasPermission2(user.roles, permission) : false;
}
export {
  RequireAuth,
  createTrpcClient,
  getAuthUrl,
  getCookie,
  getCookieDomain,
  getCookieSecureAttr,
  getToken,
  removeToken,
  setToken,
  trpc,
  trpcClient,
  useAuthStore,
  usePermission
};
